<div class="page-container productSearch-container container cannex-page">
  <app-utility-toolbar />

  <h1 class="header col-12">Rate Modeling</h1>

  <section class="intro-panel row">
    <div class="col-12">
      <p class="intro-copy">
        Compare hypothetical FIA and RILA outcomes using Cannex historical data.
        Complete the scenario inputs below to generate historical comparisons.
      </p>
    </div>
  </section>

  <form #calcForm="ngForm" class="row modeling-form" (ngSubmit)="calculate()">
    <section class="section-card col-12">
      <div class="section-header">
        <h2 class="section-title">Scenario Setup</h2>
        <p class="section-subtitle"
          >Define the base assumptions used across the model.</p
        >
      </div>

      <div class="row section-body">
        <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
          <mat-label>Annuity Type</mat-label>
          <mat-select
            #annuityTypeCtrl="ngModel"
            name="annuityType"
            required
            [(ngModel)]="formModel.annuityType"
            (ngModelChange)="onFormValueChange(calcForm)"
            (selectionChange)="resetIndexPicks()"
          >
            <mat-option value="fia"> FIA </mat-option>
            <mat-option value="rila"> RILA </mat-option>
            <mat-option value="both"> Both </mat-option>
          </mat-select>
          @if (
            annuityTypeCtrl.hasError('required') && annuityTypeCtrl.touched
          ) {
            <mat-error>Please select an annuity type</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
          <mat-label>Historical Years to Include</mat-label>
          <mat-select
            #historicalCtrl="ngModel"
            name="historicalYears"
            required
            [(ngModel)]="formModel.historicalYearsSelect"
            (ngModelChange)="onFormValueChange(calcForm)"
          >
            @for (years of historicalYears; track years) {
              <mat-option [value]="years"> {{ years }} Years </mat-option>
            }
          </mat-select>
          @if (historicalCtrl.hasError('required') && historicalCtrl.touched) {
            <mat-error>Please enter historical years to include</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
          <mat-label>Initial Premium ($) (MAX 5,000,000)</mat-label>
          <input
            #initialPremiumCtrl="ngModel"
            matInput
            id="initialPremium"
            name="initialPremium"
            required
            [maskito]="dollarMask"
            [ngModelOptions]="{ updateOn: 'blur' }"
            [(ngModel)]="formModel.initialPremium"
            (blur)="onFormValueChange(calcForm)"
          />
          @if (
            initialPremiumCtrl.hasError('required') &&
            initialPremiumCtrl.touched
          ) {
            <mat-error>Please enter an initial premium</mat-error>
          }
        </mat-form-field>

        <div class="col-12 col-lg-3 helper-copy">
          Results update after required fields are completed.
        </div>
      </div>
    </section>

    @if (annuityTypeCtrl.value === 'fia' || annuityTypeCtrl.value === 'both') {
      <section
        class="section-card col-12"
        [class.half]="annuityTypeCtrl.value === 'both'"
      >
        <div class="section-header">
          <h2 class="section-title">FIA Model</h2>
          <p class="section-subtitle"
            >Configure indexed annuity assumptions and selected indices.</p
          >
        </div>

        <div class="row section-body">
          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>FIA Contract Length</mat-label>
            <mat-select
              #fiaContractLengthCtrl="ngModel"
              name="fiaContractLength"
              required
              [(ngModel)]="formModel.fiaContractLength"
              (ngModelChange)="onFormValueChange(calcForm)"
            >
              <option value="" disabled selected>Select Contract Length</option>
              <mat-option [value]="1"> 1 Year </mat-option>
              <!-- generate from 2 to 20  -->
              @for (num of numbers20; track num) {
                <mat-option [value]="num"> {{ num }} Years </mat-option>
              }
            </mat-select>
            @if (
              fiaContractLengthCtrl.hasError('required') &&
              fiaContractLengthCtrl.touched
            ) {
              <mat-error>Please select a contract length</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>FIA Fee Value (%)</mat-label>
            <input
              #fiaFeeValueCtrl="ngModel"
              matInput
              id="fiaFeeValue"
              name="fiaFeeValue"
              required
              [maskito]="percentMask"
              [ngModelOptions]="{ updateOn: 'blur' }"
              [(ngModel)]="formModel.fiaFeeValue"
              (blur)="onFormValueChange(calcForm)"
            />
            @if (
              fiaFeeValueCtrl.hasError('required') && fiaFeeValueCtrl.touched
            ) {
              <mat-error>Please enter an FIA fee value</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>FIA Strategy</mat-label>
            <mat-select
              #strategyTypeCtrl="ngModel"
              name="fiaStrategy"
              required
              [(ngModel)]="formModel.fiaStrategySelection"
              (ngModelChange)="onFormValueChange(calcForm)"
            >
              @for (strategy of fiaStrategyList; track strategy) {
                <mat-option [value]="strategy.value">
                  {{ strategy.name }}
                </mat-option>
              }
            </mat-select>
            @if (
              strategyTypeCtrl.hasError('required') && strategyTypeCtrl.touched
            ) {
              <mat-error>Please select a strategy</mat-error>
            }
          </mat-form-field>

          @if (strategyTypeCtrl.value === 'CapWithParticipation') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Cap (%)</mat-label>
              <input
                #fiaCapCtrl="ngModel"
                matInput
                id="cap"
                name="cap"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.fiaCap"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (fiaCapCtrl.hasError('required') && fiaCapCtrl.touched) {
                <mat-error>Please enter a cap value</mat-error>
              }
            </mat-form-field>
          } @else if (strategyTypeCtrl.value === 'TriggerWithParticipation') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Trigger (%)</mat-label>
              <input
                #fiaTriggerCtrl="ngModel"
                matInput
                id="trigger"
                name="trigger"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.fiaTrigger"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                fiaTriggerCtrl.hasError('required') && fiaTriggerCtrl.touched
              ) {
                <mat-error>Please enter a trigger value</mat-error>
              }
            </mat-form-field>
          }

          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>Participation (%)</mat-label>
            <input
              #fiaParticipationCtrl="ngModel"
              matInput
              id="fiaParticipation"
              name="fiaParticipation"
              required
              [maskito]="over100PercentMask"
              [ngModelOptions]="{ updateOn: 'blur' }"
              [(ngModel)]="formModel.fiaParticipation"
              (blur)="onFormValueChange(calcForm)"
            />
            @if (
              fiaParticipationCtrl.hasError('required') &&
              fiaParticipationCtrl.touched
            ) {
              <mat-error>Please enter a participation value</mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>Minimum Guaranteed Return (%)</mat-label>
            <input
              #minimumGuaranteedReturnCtrl="ngModel"
              matInput
              id="minimumGuaranteedReturn"
              name="minimumGuaranteedReturn"
              required
              [maskito]="percentMask"
              [ngModelOptions]="{ updateOn: 'blur' }"
              [(ngModel)]="formModel.minimumGuaranteedReturn"
              (blur)="onFormValueChange(calcForm)"
            />
            @if (
              minimumGuaranteedReturnCtrl.hasError('required') &&
              minimumGuaranteedReturnCtrl.touched
            ) {
              <mat-error>Please enter a minimum guaranteed return</mat-error>
            }
          </mat-form-field>
          <div class="col-12 col-md-6 col-lg-6"></div>
          <mat-form-field appearance="outline" class="col-12 col-lg-4">
            <mat-label>FIA Calculation Period</mat-label>
            <mat-select
              #fiaCalcPeriodCtrl="ngModel"
              name="fiaCalcPeriod"
              required
              [disabled]="!fiaContractLengthCtrl.value"
              [(ngModel)]="formModel.fiaCalcPeriodSelect"
              (ngModelChange)="onFormValueChange(calcForm)"
            >
              <mat-option [value]="1"> 1 Year </mat-option>
              @if (fiaContractLengthCtrl.value > 9) {
                @for (num of numbers10; track num) {
                  <mat-option [value]="num"> {{ num }} Years </mat-option>
                }
              } @else if (fiaContractLengthCtrl.value === 2) {
                <mat-option [value]="2"> 2 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 3) {
                <mat-option [value]="3"> 3 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 4) {
                <mat-option [value]="2"> 2 Year </mat-option>
                <mat-option [value]="4"> 4 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 5) {
                <mat-option [value]="5"> 5 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 6) {
                <mat-option [value]="2"> 2 Year </mat-option>
                <mat-option [value]="3"> 3 Year </mat-option>
                <mat-option [value]="6"> 6 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 7) {
                <mat-option [value]="7"> 7 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 8) {
                <mat-option [value]="2"> 2 Year </mat-option>
                <mat-option [value]="4"> 4 Year </mat-option>
                <mat-option [value]="8"> 8 Year </mat-option>
              } @else if (fiaContractLengthCtrl.value === 9) {
                <mat-option [value]="3"> 3 Year </mat-option>
                <mat-option [value]="9"> 9 Year </mat-option>
              }
            </mat-select>
            @if (
              fiaCalcPeriodCtrl.hasError('required') &&
              fiaCalcPeriodCtrl.touched
            ) {
              <mat-error>Please enter a strategy calculation period</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-12 col-lg-8">
            <mat-label
              >FIA Strategy/Index to the following, pick up to 5 for
              {{
                formModel.annuityType === 'both' ? 'both FIA and RILA' : 'FIA'
              }}
            </mat-label>
            <mat-select
              #fiaPickCtrl="ngModel"
              name="fiaPick"
              multiple
              required
              [(ngModel)]="formModel.fiaIndexPick"
              (closed)="onFormValueChange(calcForm)"
            >
              @for (fund of pickList; track fund) {
                <mat-option
                  [value]="fund"
                  [disabled]="
                    formModel.annuityType === 'both'
                      ? formModel.rilaIndexPick?.length +
                          formModel.fiaIndexPick?.length >=
                          5 && !formModel.fiaIndexPick.includes(fund)
                      : formModel.fiaIndexPick?.length >= 5 &&
                        !formModel.fiaIndexPick.includes(fund)
                  "
                >
                  {{ fund }}
                </mat-option>
              }
            </mat-select>
            @if (fiaPickCtrl.hasError('required') && fiaPickCtrl.touched) {
              <mat-error
                >Please select up to 5 strategies in total for both FIA and
                RILA</mat-error
              >
            }
          </mat-form-field>
        </div>
      </section>
    }

    @if (annuityTypeCtrl.value === 'rila' || annuityTypeCtrl.value === 'both') {
      <section
        class="section-card col-12"
        [class.half]="annuityTypeCtrl.value === 'both'"
      >
        <div class="section-header">
          <h2 class="section-title">RILA Model</h2>
          <p class="section-subtitle"
            >Configure structured annuity assumptions and selected indices.</p
          >
        </div>

        <div class="row section-body">
          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>RILA Contract Length</mat-label>
            <mat-select
              #rilaContractLengthCtrl="ngModel"
              name="rilaContractLength"
              required
              [(ngModel)]="formModel.rilaContractLength"
              (ngModelChange)="onFormValueChange(calcForm)"
            >
              <option value="" disabled selected>Select Contract Length</option>
              <mat-option [value]="1"> 1 Year </mat-option>
              <!-- generate from 2 to 20  -->
              @for (num of numbers20; track num) {
                <mat-option [value]="num"> {{ num }} Years </mat-option>
              }
            </mat-select>
            @if (
              rilaContractLengthCtrl.hasError('required') &&
              rilaContractLengthCtrl.touched
            ) {
              <mat-error>Please select a contract length</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>RILA Fee Value (%)</mat-label>
            <input
              #rilaFeeValueCtrl="ngModel"
              matInput
              id="rilaFeeValue"
              name="rilaFeeValue"
              required
              [maskito]="percentMask"
              [ngModelOptions]="{ updateOn: 'blur' }"
              [(ngModel)]="formModel.rilaFeeValue"
              (blur)="onFormValueChange(calcForm)"
            />
            @if (
              rilaFeeValueCtrl.hasError('required') && rilaFeeValueCtrl.touched
            ) {
              <mat-error>Please enter a RILA fee value</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-12 col-md-6 col-lg-3">
            <mat-label>RILA Strategy</mat-label>
            <mat-select
              #rilaStrategyCtrl="ngModel"
              name="rilaStrategy"
              required
              [(ngModel)]="formModel.rilaStrategySelection"
              (ngModelChange)="onFormValueChange(calcForm)"
            >
              @for (strategy of rilaStrategyList; track strategy) {
                <mat-option [value]="strategy.value">
                  {{ strategy.name }}
                </mat-option>
              }
            </mat-select>
            @if (
              rilaStrategyCtrl.hasError('required') && rilaStrategyCtrl.touched
            ) {
              <mat-error>Please select a strategy</mat-error>
            }
          </mat-form-field>
          @if (rilaStrategyCtrl.value === 'CapWithBuffer') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Cap (%)</mat-label>
              <input
                #rilaCapCtrl="ngModel"
                matInput
                id="rilaCap"
                name="rilaCap"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaCap"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (rilaCapCtrl.hasError('required') && rilaCapCtrl.touched) {
                <mat-error>Please enter a cap value</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Buffer (%)</mat-label>
              <input
                #rilaBufferCtrl="ngModel"
                matInput
                id="rilaBuffer"
                name="rilaBuffer"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaBuffer"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaBufferCtrl.hasError('required') && rilaBufferCtrl.touched
              ) {
                <mat-error>Please enter a buffer value</mat-error>
              }
            </mat-form-field>
          } @else if (rilaStrategyCtrl.value === 'CapWithFloor') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Cap (%)</mat-label>
              <input
                #rilaCapCtrl="ngModel"
                matInput
                id="rilaCap"
                name="rilaCap"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaCap"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (rilaCapCtrl.hasError('required') && rilaCapCtrl.touched) {
                <mat-error>Please enter a cap value</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Floor (%)</mat-label>
              <input
                #rilaFloorCtrl="ngModel"
                matInput
                id="rilaFloor"
                name="rilaFloor"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaFloor"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaFloorCtrl.hasError('required') && rilaFloorCtrl.touched
              ) {
                <mat-error>Please enter a floor value</mat-error>
              }
            </mat-form-field>
          } @else if (rilaStrategyCtrl.value === 'TriggerWithBuffer') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Trigger (%)</mat-label>
              <input
                #rilaTriggerCtrl="ngModel"
                matInput
                id="rilaTrigger"
                name="rilaTrigger"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaTrigger"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaTriggerCtrl.hasError('required') && rilaTriggerCtrl.touched
              ) {
                <mat-error>Please enter a trigger value</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Buffer (%)</mat-label>
              <input
                #rilaBufferCtrl="ngModel"
                matInput
                id="rilaBuffer"
                name="rilaBuffer"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaBuffer"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaBufferCtrl.hasError('required') && rilaBufferCtrl.touched
              ) {
                <mat-error>Please enter a buffer value</mat-error>
              }
            </mat-form-field>
          } @else if (rilaStrategyCtrl.value === 'TriggerWithFloor') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Trigger (%)</mat-label>
              <input
                #rilaTriggerCtrl="ngModel"
                matInput
                id="rilaTrigger"
                name="rilaTrigger"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaTrigger"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaTriggerCtrl.hasError('required') && rilaTriggerCtrl.touched
              ) {
                <mat-error>Please enter a trigger value</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Floor (%)</mat-label>
              <input
                #rilaFloorCtrl="ngModel"
                matInput
                id="rilaFloor"
                name="rilaFloor"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaFloor"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaFloorCtrl.hasError('required') && rilaFloorCtrl.touched
              ) {
                <mat-error>Please enter a floor value</mat-error>
              }
            </mat-form-field>
          } @else if (rilaStrategyCtrl.value === 'ParticipationWithBuffer') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Participation (%)</mat-label>
              <input
                #rilaParticipationCtrl="ngModel"
                matInput
                id="rilaParticipation"
                name="rilaParticipation"
                required
                [maskito]="over100PercentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaParticipation"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaParticipationCtrl.hasError('required') &&
                rilaParticipationCtrl.touched
              ) {
                <mat-error>Please enter a participation value</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Buffer (%)</mat-label>
              <input
                #rilaBufferCtrl="ngModel"
                matInput
                id="rilaBuffer"
                name="rilaBuffer"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaBuffer"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaBufferCtrl.hasError('required') && rilaBufferCtrl.touched
              ) {
                <mat-error>Please enter a buffer value</mat-error>
              }
            </mat-form-field>
          } @else if (rilaStrategyCtrl.value === 'ParticipationWithFloor') {
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Participation (%)</mat-label>
              <input
                #rilaParticipationCtrl="ngModel"
                matInput
                id="rilaParticipation"
                name="rilaParticipation"
                required
                [maskito]="over100PercentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaParticipation"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaParticipationCtrl.hasError('required') &&
                rilaParticipationCtrl.touched
              ) {
                <mat-error>Please enter a participation value</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              appearance="outline"
              class="col-12 col-md-6 col-lg-3"
            >
              <mat-label>Floor (%)</mat-label>
              <input
                #rilaFloorCtrl="ngModel"
                matInput
                id="rilaFloor"
                name="rilaFloor"
                required
                [maskito]="percentMask"
                [ngModelOptions]="{ updateOn: 'blur' }"
                [(ngModel)]="formModel.rilaFloor"
                (blur)="onFormValueChange(calcForm)"
              />
              @if (
                rilaFloorCtrl.hasError('required') && rilaFloorCtrl.touched
              ) {
                <mat-error>Please enter a floor value</mat-error>
              }
            </mat-form-field>
          } @else {
            <div class="col-12 col-md-6 col-lg-4"></div>
          }
          <div class="col-12 col-md-6 col-lg-6"></div>
          <mat-form-field appearance="outline" class="col-12 col-lg-4">
            <mat-label>RILA Calculation Period</mat-label>
            <mat-select
              #rilaCalcPeriodCtrl="ngModel"
              name="rilaCalcPeriod"
              required
              [disabled]="!rilaContractLengthCtrl.value"
              [(ngModel)]="formModel.rilaCalcPeriodSelect"
              (ngModelChange)="onFormValueChange(calcForm)"
            >
              <mat-option [value]="1"> 1 Year </mat-option>
              @if (rilaContractLengthCtrl.value > 9) {
                @for (num of numbers10; track num) {
                  <mat-option [value]="num"> {{ num }} Years </mat-option>
                }
              } @else if (rilaContractLengthCtrl.value === 2) {
                <mat-option [value]="2"> 2 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 3) {
                <mat-option [value]="3"> 3 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 4) {
                <mat-option [value]="2"> 2 Year </mat-option>
                <mat-option [value]="4"> 4 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 5) {
                <mat-option [value]="5"> 5 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 6) {
                <mat-option [value]="2"> 2 Year </mat-option>
                <mat-option [value]="3"> 3 Year </mat-option>
                <mat-option [value]="6"> 6 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 7) {
                <mat-option [value]="7"> 7 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 8) {
                <mat-option [value]="2"> 2 Year </mat-option>
                <mat-option [value]="4"> 4 Year </mat-option>
                <mat-option [value]="8"> 8 Year </mat-option>
              } @else if (rilaContractLengthCtrl.value === 9) {
                <mat-option [value]="3"> 3 Year </mat-option>
                <mat-option [value]="9"> 9 Year </mat-option>
              }
            </mat-select>
            @if (
              rilaCalcPeriodCtrl.hasError('required') &&
              rilaCalcPeriodCtrl.touched
            ) {
              <mat-error>Please enter a strategy calculation period</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-12 col-lg-8">
            <mat-label
              >RILA Strategy/Index to the following, pick up to 5 for
              {{
                formModel.annuityType === 'both' ? 'both FIA and RILA' : 'RILA'
              }}</mat-label
            >
            <mat-select
              #rilaPickCtrl="ngModel"
              name="rilaPick"
              multiple
              required
              [(ngModel)]="formModel.rilaIndexPick"
              (closed)="onFormValueChange(calcForm)"
            >
              @for (fund of pickList; track fund) {
                <mat-option
                  [value]="fund"
                  [disabled]="
                    formModel.annuityType === 'both'
                      ? formModel.rilaIndexPick?.length +
                          formModel.fiaIndexPick?.length >=
                          5 && !formModel.rilaIndexPick.includes(fund)
                      : formModel.rilaIndexPick?.length >= 5 &&
                        !formModel.rilaIndexPick.includes(fund)
                  "
                >
                  {{ fund }}
                </mat-option>
              }
            </mat-select>
            @if (rilaPickCtrl.hasError('required') && rilaPickCtrl.touched) {
              <mat-error
                >Please select up to 5 strategies in total for both FIA and
                RILA</mat-error
              >
            }
          </mat-form-field>
        </div>
      </section>
    }
  </form>

  @if (calcForm.valid) {
    <section class="results-section row">
      <div class="col-12">
        <div class="results-header">
          <h2 class="section-title">Results</h2>
          <p class="section-subtitle"
            >Modeled growth, term outcomes, and annualized return
            comparisons.</p
          >
        </div>
      </div>

      <div class="col-12 col-xl-6">
        <div class="result-card chart-card">
          <div class="result-card-header">Growth Projection</div>
          <highcharts-chart
            style="width: 100%; display: block; overflow: hidden"
            [options]="lineChartOptions"
            (chartInstance)="onLineChartInit($event, calcForm)"
          />
        </div>
      </div>

      <div class="col-12 col-xl-6">
        <div class="result-card chart-card">
          <div class="result-card-header">Annuity Term Outcomes</div>
          <highcharts-chart
            style="width: 100%; display: block; overflow: hidden"
            [options]="columnChartOptions"
            (chartInstance)="onColumnChartInit($event, calcForm)"
          />
        </div>
      </div>

      <div class="col-12 col-xl-6">
        <div class="result-card table-card">
          <div class="result-card-header"
            >Annualized Returns By Contract Term</div
          >
          <table>
            <thead>
              <tr>
                <th>Index Name</th>
                <th>Average</th>
                <th>Best</th>
                <th>Worst</th>
                <th>Median</th>
                <th>Average # of Positive Terms per Contract</th>
                <th>Average # of Negative Terms per Contract</th>
              </tr>
            </thead>
            <tbody>
              @for (data of indices; track data.name) {
                <tr>
                  <td>{{ data.name }}</td>
                  <td>{{ data.average }}</td>
                  <td>{{ data.best }}</td>
                  <td>{{ data.worst }}</td>
                  <td>{{ data.median }}</td>
                  <td>{{ data.avgPositive }}</td>
                  <td>{{ data.avgNegative }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <div class="col-12 col-xl-6">
        <div class="result-card chart-card">
          <div class="result-card-header">Index Positive Window</div>
          <highcharts-chart
            style="width: 100%; display: block; overflow: hidden"
            [options]="stackedColumnChartOptions"
            (chartInstance)="onStackedColumnChartInit($event, calcForm)"
          />
        </div>
      </div>
    </section>
  } @else {
    <section class="row">
      <div class="col-12">
        <div class="placeholder-panel">
          Complete the required fields above to generate results.
        </div>
      </div>
    </section>
  }

  <ngx-loading [show]="loading" />
</div>
